import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquareIcon, SendIcon, RefreshCwIcon, AlertCircleIcon, ClockIcon, PlayIcon, BotIcon, HandIcon, CheckCircleIcon, UserPlusIcon, InboxIcon, ChevronDownIcon, UserIcon, ZapIcon, XIcon, CalendarIcon, SettingsIcon, PlusIcon, Trash2Icon, MapPinIcon, SearchIcon, SaveIcon, ArrowLeftIcon, MoreVerticalIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Tabs } from '../ui/Tabs';
import { supabase } from '../../utils/supabase';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../contexts/ConfirmContext';

// ----- Tipos locales -----
type ConvStatus = 'bot' | 'pending' | 'assigned' | 'resolved';
interface BotConversation {
  id: string;
  patient_id: string | null;
  channel: string;
  sender_id: string;
  phone: string | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  window_expires_at: string | null;
  bot_paused: boolean;
  status: ConvStatus;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_at: string | null;
  resolved_at: string | null;
  created_at: string;
  patient?: {
    name: string;
  } | null;
  lastMessage?: BotMessage | null;
}
interface BotMessage {
  id: string;
  conversation_id: string;
  direction: 'in' | 'out';
  body: string;
  type: string;
  status: string;
  error_message: string | null;
  sent_by: string | null;
  created_at: string;
  payload?: Record<string, unknown>;
}
interface BotOutreachRow {
  id: string;
  patient_id: string;
  campaign_type: string;
  template_name: string | null;
  step: number;
  status: string;
  sent_at: string | null;
  responded_at: string | null;
  created_at: string;
  error_message: string | null;
  patient?: {
    name: string;
  } | null;
}
interface BotTemplate {
  id: string;
  name: string;
  provider_sid: string | null;
  language: string;
  channel: string;
  category: string;
  campaign_type: string | null;
  body_preview: string;
  status: string;
  active: boolean;
}
type ListFilter = 'all' | 'pending' | 'mine' | 'bot' | 'resolved';
interface PatientAppt {
  id: string;
  date: string;
  time: string;
  status: string;
  service?: {
    name: string;
  } | null;
  doctor?: {
    name: string;
  } | null;
}
const READ_STATE_KEY = 'bot-omnichannel-read-state';
const CLINIC_INFO_KEY = 'bot_clinic_info';

// Información de la clínica editable desde la pestaña Configuración y consultada
// por el bot vía la herramienta get_clinic_info.
interface ClinicInfo {
  hours: string;
  address: string;
  maps_url: string;
  phone: string;
  payment_methods: string;
  faq: {
    q: string;
    a: string;
  }[];
}
const EMPTY_CLINIC_INFO: ClinicInfo = {
  hours: '',
  address: '',
  maps_url: '',
  phone: '',
  payment_methods: '',
  faq: []
};

// Respuestas rápidas para agentes. La de horario/ubicación se completa con la
// info de clínica configurada (si existe); si no, cae a un texto genérico.
function buildQuickReplies(clinic: ClinicInfo): {
  label: string;
  text: string;
}[] {
  const horario = clinic.hours.trim() ? `Nuestro horario de atención es ${clinic.hours.trim()}` : 'Con gusto te comparto nuestro horario de atención. Permíteme un momento.';
  const ubicacion = clinic.address.trim() ? `Estamos ubicados en ${clinic.address.trim()}.${clinic.maps_url.trim() ? ` Aquí la ubicación en el mapa: ${clinic.maps_url.trim()}` : ''}` : 'Con gusto te comparto nuestra ubicación. Permíteme un momento.';
  return [{
    label: '👋 Saludo',
    text: '¡Hola! Soy del equipo de Savidna, con gusto te ayudo. ¿En qué puedo asistirte?'
  }, {
    label: '🕒 Horario',
    text: horario
  }, {
    label: '📍 Ubicación',
    text: ubicacion
  }, {
    label: '💲 Precios',
    text: 'Con gusto te comparto los precios. ¿Sobre cuál servicio o tratamiento te gustaría información?'
  }, {
    label: '📅 Agendar',
    text: 'Perfecto, podemos agendarte una cita. ¿Qué día y horario te queda mejor?'
  }, {
    label: '🙏 Despedida',
    text: '¡Gracias por escribirnos! Quedamos atentos a cualquier otra consulta. Que tengas un excelente día. 😊'
  }];
}

// ----- Helpers -----
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}
function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `hace ${days}d`;
}
function channelLabel(c: string): string {
  return {
    whatsapp: 'WhatsApp',
    messenger: 'Messenger',
    instagram: 'Instagram',
    voice: 'Voz'
  }[c] ?? c;
}
function campaignLabel(c: string): string {
  return {
    appointment_reminder: 'Recordatorio',
    inactive_7d: 'Inactivo 7d',
    inactive_followup: 'Seguimiento',
    inactive_30d: 'Inactivo 30d',
    birthday: 'Cumpleaños',
    anniversary: 'Aniversario',
    incomplete_treatment: 'Tratamiento',
    clinical_review: 'Revisión clínica'
  }[c] ?? c;
}
function outreachStatusVariant(s: string): 'success' | 'danger' | 'warning' | 'info' | 'default' {
  if (s === 'sent') return 'info';
  if (s === 'responded') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'pending') return 'warning';
  return 'default';
}
function isWindowOpen(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() > Date.now();
}
function statusOf(c: BotConversation): ConvStatus {
  return c.status ?? (c.bot_paused ? 'assigned' : 'bot');
}
function messagePreview(m: BotMessage | undefined | null): string {
  if (!m) return 'Sin mensajes';
  if (m.type === 'audio_transcribed') return `🎙️ ${m.body}`;
  if (m.type === 'media' || m.body === '[media]') return '📎 Archivo recibido';
  if (m.type === 'image') return '🖼️ Imagen';
  if (m.type === 'document') return '📄 Documento';
  return m.body || '(vacío)';
}

// Genera variantes de un teléfono para buscar en patients.phone (que puede
// estar guardado en formatos heterogéneos: +18091234567, 8091234567, 809-123-4567).
function phoneSearchVariants(phone: string): string[] {
  const digits = phone.replace(/[^\d]/g, '');
  const variants = new Set<string>();
  variants.add(phone);
  variants.add(digits);
  if (digits.startsWith('1') && digits.length === 11) {
    const local10 = digits.slice(1);
    variants.add(local10);
    variants.add(`+1${local10}`);
    variants.add(`${local10.slice(0, 3)}-${local10.slice(3, 6)}-${local10.slice(6)}`);
    variants.add(`(${local10.slice(0, 3)}) ${local10.slice(3, 6)}-${local10.slice(6)}`);
  }
  return Array.from(variants);
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Escapa HTML para evitar inyección antes de aplicar el formato de WhatsApp.
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Convierte el formato de WhatsApp (*negrita*, _itálica_, ~tachado~, `mono`)
// y enlaces a HTML para mostrarlo correctamente en la burbuja del chat.
function formatWhatsApp(raw: string): string {
  let s = escapeHtml(raw);
  // Enlaces http(s) → <a>
  s = s.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="underline">$1</a>');
  // *negrita* (no si hay espacio pegado al asterisco)
  s = s.replace(/(^|[\s.,;:!¡¿(])\*(?!\s)([^*\n]+?)(?<!\s)\*(?=$|[\s.,;:!?)])/g, '$1<strong>$2</strong>');
  // _itálica_
  s = s.replace(/(^|[\s.,;:!¡¿(])_(?!\s)([^_\n]+?)(?<!\s)_(?=$|[\s.,;:!?)])/g, '$1<em>$2</em>');
  // ~tachado~
  s = s.replace(/(^|[\s.,;:!¡¿(])~(?!\s)([^~\n]+?)(?<!\s)~(?=$|[\s.,;:!?)])/g, '$1<del>$2</del>');
  // `monoespacio`
  s = s.replace(/`([^`\n]+?)`/g, '<code class="px-1 rounded bg-black/10 dark:bg-white/10 text-[0.85em]">$1</code>');
  return s;
}

// Etiqueta de día para los separadores del hilo: "Hoy", "Ayer" o "12 may".
function dayLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return 'Hoy';
  if (sameDay(d, yesterday)) return 'Ayer';
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric'
  });
}

// Solo la hora (HH:MM) para el pie de cada burbuja.
function timeOnly(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

// Extrae la URL de imagen mostrable de un mensaje (flyers salientes con
// payload.image_url son URLs públicas; las de Twilio entrantes requieren auth
// y no se pueden incrustar). Devuelve null si no hay imagen pública.
function publicImageUrl(m: BotMessage): string | null {
  const p = (m.payload ?? {}) as Record<string, unknown>;
  const url = p.image_url;
  if (typeof url === 'string' && /^https?:\/\//i.test(url)) return url;
  return null;
}

// Sonido de alerta cuando el bot escala una conversación a un humano. Usa la
// Web Audio API (sin assets): dos tonos cortos ascendentes, discretos. Se
// ignora cualquier error (p. ej. si el navegador bloquea audio sin interacción).
function playEscalationChime(): void {
  try {
    const Ctx = (window as unknown as {
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    }).AudioContext || (window as unknown as {
      webkitAudioContext?: typeof AudioContext;
    }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1175].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = now + i * 0.16;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.16);
    });
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch {

    // Audio no disponible: la alerta visual y la notificación siguen funcionando.
  }
} // Notificación del navegador (si el usuario concedió permiso). No fuerza el
// pedido aquí; lo solicitamos una vez al montar el módulo.
function showBrowserNotification(title: string, body: string): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    new Notification(title, {
      body,
      tag: 'bot-escalation'
    });
  } catch {

    // Algunos navegadores lanzan si se llama fuera de un gesto del usuario.
  }
} // ===== Componente principal =====
export function BotOmnichannel() {
  const {
    addNotification
  } = useNotifications();
  const {
    user,
    users
  } = useAuth();
  const {
    confirm
  } = useConfirm();
  const [activeTab, setActiveTab] = useState('conversations');
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<BotConversation[]>([]);
  const [outreach, setOutreach] = useState<BotOutreachRow[]>([]);
  const [templates, setTemplates] = useState<BotTemplate[]>([]);
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [chatSearch, setChatSearch] = useState('');
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [assignMenuOpen, setAssignMenuOpen] = useState(false);
  const [assignMenuPos, setAssignMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  // Menú "más acciones" (Ficha/Tomar/Asignar/Resolver/Bot) para pantallas estrechas.
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [moreMenuPos, setMoreMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const [quickRepliesOpen, setQuickRepliesOpen] = useState(false);
  const [quickRepliesPos, setQuickRepliesPos] = useState<{
    bottom: number;
    left: number;
  } | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [patientAppts, setPatientAppts] = useState<PatientAppt[]>([]);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [clinicInfo, setClinicInfo] = useState<ClinicInfo>(EMPTY_CLINIC_INFO);
  const [savingClinic, setSavingClinic] = useState(false);
  const [clinicSaved, setClinicSaved] = useState(false);
  // Conversaciones recién escaladas por el bot que aún no se han atendido (para
  // resaltarlas en la lista). Se limpian al abrirlas o cuando dejan de estar
  // pendientes.
  const [freshEscalations, setFreshEscalations] = useState<Set<string>>(new Set());
  // Estado de lectura por conversación (persistido en localStorage).
  const [readState, setReadState] = useState<Record<string, number>>(() => {
    try {
      return JSON.parse(localStorage.getItem(READ_STATE_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // ¿El usuario está pegado al fondo del chat? Si subió a leer historial, no lo
  // arrastramos al fondo cuando llega un mensaje nuevo.
  const stickToBottomRef = useRef(true);
  // Última conversación abierta: al cambiar de chat siempre saltamos al fondo.
  const lastConvRef = useRef<string | null>(null);
  const assignBtnRef = useRef<HTMLButtonElement>(null);
  const assignMenuRef = useRef<HTMLDivElement>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const quickRepliesBtnRef = useRef<HTMLButtonElement>(null);
  const quickRepliesMenuRef = useRef<HTMLDivElement>(null);
  // Ref para que el callback de realtime (que se monta una sola vez) siempre
  // vea la conversación abierta actual, no el valor capturado al montar.
  const selectedConvIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedConvIdRef.current = selectedConvId;
  }, [selectedConvId]);

  // Marca si el usuario está cerca del fondo (margen de 120px) cada vez que
  // hace scroll en el hilo. Así sabemos si debemos auto-bajar al llegar mensajes.
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Auto-scroll: mueve SOLO el contenedor del hilo (nunca la página), de forma
  // SÍNCRONA antes de pintar (useLayoutEffect) para que no se vea ningún salto.
  // Al cambiar de conversación siempre baja al fondo; al actualizarse el hilo
  // (mensaje nuevo o refresco) baja solo si el usuario ya estaba pegado al fondo
  // (si subió a leer historial, no lo arrastramos). Depende de `messages` (la
  // referencia) para re-anclar también cuando el contenido cambia sin variar la
  // cantidad.
  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const convChanged = lastConvRef.current !== selectedConvId;
    lastConvRef.current = selectedConvId;
    if (convChanged) stickToBottomRef.current = true;
    if (convChanged || stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, selectedConvId, loadingMessages]);

  // Marcar conversación abierta como leída
  const markRead = useCallback((convId: string) => {
    setReadState(prev => {
      const next = {
        ...prev,
        [convId]: Date.now()
      };
      try {
        localStorage.setItem(READ_STATE_KEY, JSON.stringify(next));
      } catch {

        // localStorage lleno o no disponible: ignoramos
      }
      return next;
    });
  }, []);
  useEffect(() => {
    if (selectedConvId) markRead(selectedConvId);
  }, [selectedConvId, messages.length, markRead]);

  // Al abrir una conversación recién escalada, deja de resaltarla como "nueva"
  // (el agente ya la vio); el banner de "pendiente" sigue mientras no la tome.
  useEffect(() => {
    if (!selectedConvId) return;
    setFreshEscalations(prev => {
      if (!prev.has(selectedConvId)) return prev;
      const next = new Set(prev);
      next.delete(selectedConvId);
      return next;
    });
  }, [selectedConvId]);

  // Una conversación está "sin leer" si su último mensaje es entrante y es más
  // reciente que la última vez que la abrimos.
  const isUnread = useCallback((c: BotConversation): boolean => {
    const lm = c.lastMessage;
    if (!lm || lm.direction !== 'in') return false;
    if (c.id === selectedConvId) return false;
    const lastReadAt = readState[c.id] ?? 0;
    return new Date(lm.created_at).getTime() > lastReadAt;
  }, [readState, selectedConvId]);

  // Cerrar el menú de asignar al hacer clic fuera
  useEffect(() => {
    if (!assignMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (assignMenuRef.current && !assignMenuRef.current.contains(e.target as Node)) {
        setAssignMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [assignMenuOpen]);

  // Cerrar el menú "más acciones" al hacer clic fuera (ignora su propio botón)
  useEffect(() => {
    if (!moreMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (moreMenuRef.current && !moreMenuRef.current.contains(t) && moreBtnRef.current && !moreBtnRef.current.contains(t)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [moreMenuOpen]);

  // Cerrar el menú de respuestas rápidas al hacer clic fuera
  useEffect(() => {
    if (!quickRepliesOpen) return;
    const onClick = (e: MouseEvent) => {
      if (quickRepliesMenuRef.current && !quickRepliesMenuRef.current.contains(e.target as Node) && quickRepliesBtnRef.current && !quickRepliesBtnRef.current.contains(e.target as Node)) {
        setQuickRepliesOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [quickRepliesOpen]);

  // Mantenemos addNotification en ref para que refreshAll no se recree y no
  // re-suscriba realtime en cada render.
  const notifyRef = useRef(addNotification);
  useEffect(() => {
    notifyRef.current = addNotification;
  }, [addNotification]);

  // ----- Alertas de escalado a humano -----
  // El estado 'pending' SOLO lo fija el bot cuando escala una conversación a un
  // asesor (el cliente pidió hablar con alguien o el bot no pudo resolver). Por
  // eso, cualquier transición hacia 'pending' es una escalada que el equipo debe
  // ver de inmediato: la anunciamos con notificación + sonido + banner.
  const prevPendingRef = useRef<Set<string> | null>(null);

  // Pedimos permiso de notificaciones del navegador una sola vez.
  useEffect(() => {
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch {

      // Navegador sin soporte: seguimos con alerta in-app + sonido.
    }
  }, []);
  useEffect(() => {
    const currentPending = new Set(conversations.filter(c => statusOf(c) === 'pending').map(c => c.id));
    // Primera carga: sembramos sin alertar (no queremos avisar de pendientes
    // que ya existían antes de abrir el panel).
    if (prevPendingRef.current === null) {
      prevPendingRef.current = currentPending;
      setFreshEscalations(currentPending);
      return;
    }
    const prev = prevPendingRef.current;
    const newlyEscalated = conversations.filter(c => currentPending.has(c.id) && !prev.has(c.id));
    if (newlyEscalated.length > 0) {
      playEscalationChime();
      for (const c of newlyEscalated) {
        const who = c.patient?.name || c.phone || c.sender_id;
        const title = '🙋 El bot escaló una conversación';
        const message = `${who} necesita un asesor. Tómala o asígnala desde la bandeja.`;
        notifyRef.current?.({
          type: 'system' as const,
          title,
          message,
          relatedId: c.id
        });
        showBrowserNotification(title, message);
      }
      setFreshEscalations(prevSet => {
        const next = new Set(prevSet);
        newlyEscalated.forEach(c => next.add(c.id));
        return next;
      });
    }
    // Quitar del resaltado las que ya dejaron de estar pendientes (atendidas).
    setFreshEscalations(prevSet => {
      let changed = false;
      const next = new Set(prevSet);
      for (const id of prevSet) {
        if (!currentPending.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prevSet;
    });
    prevPendingRef.current = currentPending;
  }, [conversations]);
  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [convRes, outRes, tplRes] = await Promise.all([supabase.from('bot_conversations').select('*, patient:patients(name)').order('last_inbound_at', {
        ascending: false,
        nullsFirst: false
      }).limit(100), supabase.from('bot_outreach_log').select('*, patient:patients(name)').order('created_at', {
        ascending: false
      }).limit(100), supabase.from('bot_templates').select('*').order('campaign_type')]);
      if (convRes.error) throw convRes.error;
      if (outRes.error) throw outRes.error;
      if (tplRes.error) throw tplRes.error;
      const convs = (convRes.data ?? []) as BotConversation[];

      // Cargar el último mensaje de cada conversación para mostrar preview
      const convIds = convs.map(c => c.id);
      if (convIds.length > 0) {
        const {
          data: lastMsgs
        } = await supabase.from('bot_messages').select('conversation_id, direction, body, type, created_at, sent_by').in('conversation_id', convIds).order('created_at', {
          ascending: false
        });
        const byConv = new Map<string, BotMessage>();
        for (const m of (lastMsgs ?? []) as BotMessage[]) {
          if (!byConv.has(m.conversation_id)) byConv.set(m.conversation_id, m);
        }
        convs.forEach(c => {
          c.lastMessage = byConv.get(c.id) ?? null;
        });
      }

      // El teléfono es la fuente de verdad de quién es el paciente: re-matcheamos
      // TODAS las conversaciones con teléfono contra patients.phone (no solo las
      // sin vincular). Así, si reasignas un número de un paciente a otro en el
      // CRM, el chat muestra el nuevo dueño del número y no el nombre obsoleto
      // que dejó el patient_id antiguo.
      const withPhone = convs.filter(c => c.phone);
      if (withPhone.length > 0) {
        const allVariants: string[] = [];
        for (const c of withPhone) {
          allVariants.push(...phoneSearchVariants(c.phone!));
        }
        if (allVariants.length > 0) {
          const {
            data: matchedPatients
          } = await supabase.from('patients').select('id, name, phone').in('phone', allVariants);
          for (const c of withPhone) {
            const variants = phoneSearchVariants(c.phone!);
            const match = (matchedPatients ?? []).find(p => variants.includes(p.phone));
            if (match) {
              // Un paciente posee este número ahora mismo: usarlo aunque el
              // patient_id guardado apunte a otro (link obsoleto).
              c.patient = {
                name: match.name
              };
              c.patient_id = match.id;
            } else if (!c.patient_id) {
              // Nadie posee el número y la conversación no estaba vinculada:
              // sin paciente que mostrar.
              c.patient = null;
            }
          }
        }
      }
      setConversations(convs);
      setOutreach((outRes.data ?? []) as BotOutreachRow[]);
      setTemplates((tplRes.data ?? []) as BotTemplate[]);

      // Mantener el hilo abierto en vivo: en cada refresco (montaje, realtime o
      // botón "Actualizar") recargamos también los mensajes de la conversación
      // abierta, no solo el preview del lateral.
      const openId = selectedConvIdRef.current;
      if (openId) {
        // Los 200 MÁS RECIENTES (desc + limit) y luego ascendente para mostrar.
        // Con ascending+limit se traían los 200 más ANTIGUOS, lo que mandaba el
        // hilo al inicio de la conversación al refrescar.
        const {
          data: openMsgs
        } = await supabase.from('bot_messages').select('*').eq('conversation_id', openId).order('created_at', {
          ascending: false
        }).limit(200);
        setMessages(((openMsgs ?? []) as BotMessage[]).slice().reverse());
      }
    } catch (err) {
      console.error('[BotOmnichannel] refresh error', err);
      notifyRef.current?.({
        type: 'system' as const,
        title: 'Error cargando datos del bot',
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga inicial + realtime
  useEffect(() => {
    refreshAll();
    // refreshAll recarga el lateral y el chat abierto. Escuchamos tanto
    // bot_messages como bot_conversations (el bot actualiza la conversación en
    // cada mensaje), así el panel se mantiene en vivo aunque alguna de las dos
    // tablas no esté en la publicación de realtime.
    const channel = supabase.channel('bot-omnichannel').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'bot_messages'
    }, refreshAll).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'bot_conversations'
    }, refreshAll).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'bot_outreach_log'
    }, refreshAll).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'patients'
    }, refreshAll).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAll]);
  const loadMessages = async (convId: string) => {
    setLoadingMessages(true);
    try {
      // Traemos los 200 MÁS RECIENTES (orden desc + limit) y luego los
      // reordenamos ascendente para mostrar. Con ascending+limit se devolvían
      // los 200 más ANTIGUOS, dejando fuera los mensajes nuevos en hilos largos.
      const {
        data,
        error
      } = await supabase.from('bot_messages').select('*').eq('conversation_id', convId).order('created_at', {
        ascending: false
      }).limit(200);
      if (error) throw error;
      const rows = ((data ?? []) as BotMessage[]).slice().reverse();
      setMessages(rows);
    } catch (err) {
      console.error('[BotOmnichannel] loadMessages error', err);
    } finally {
      setLoadingMessages(false);
    }
  };
  useEffect(() => {
    if (selectedConvId) loadMessages(selectedConvId);else setMessages([]);
  }, [selectedConvId]);

  // Refresco silencioso del chat abierto (red de seguridad): cada 4s revisamos
  // si hay mensajes nuevos en la conversación abierta y solo actualizamos el
  // estado si algo cambió. Garantiza que el hilo se mantenga al día aunque el
  // realtime de bot_messages no esté habilitado en la base de datos. No usa
  // setLoadingMessages para no provocar parpadeo ni saltos de scroll.
  useEffect(() => {
    if (!selectedConvId) return;
    let cancelled = false;
    const poll = async () => {
      const {
        data,
        error
      } = await supabase.from('bot_messages').select('*').eq('conversation_id', selectedConvId).order('created_at', {
        ascending: false
      }).limit(200);
      if (cancelled || error || !data) return;
      const next = (data as BotMessage[]).slice().reverse();
      setMessages(prev => {
        const last = prev[prev.length - 1];
        const nextLast = next[next.length - 1];
        // Sin cambios en cantidad, último id ni estado del último mensaje: no
        // tocamos el estado para evitar renders innecesarios.
        if (prev.length === next.length && last?.id === nextLast?.id && last?.status === nextLast?.status) {
          return prev;
        }
        return next;
      });
    };
    const interval = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedConvId]);

  // Cargar citas del paciente vinculado (contexto del chat)
  useEffect(() => {
    const conv = conversations.find(c => c.id === selectedConvId);
    const pid = conv?.patient_id;
    if (!pid) {
      setPatientAppts([]);
      return;
    }
    let active = true;
    setLoadingCtx(true);
    (async () => {
      try {
        const {
          data
        } = await supabase.from('appointments').select('id, date, time, status, service:services(name), doctor:doctors(name)').eq('patient_id', pid).order('date', {
          ascending: false
        }).limit(30);
        if (active) setPatientAppts((data ?? []) as unknown as PatientAppt[]);
      } catch (err) {
        console.error('[BotOmnichannel] patient context error', err);
        if (active) setPatientAppts([]);
      } finally {
        if (active) setLoadingCtx(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedConvId, conversations]);

  // Derivar próxima cita / última visita / total
  const patientCtx = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = patientAppts.filter(a => a.date >= today && (a.status === 'programada' || a.status === 'reprogramada')).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
    const completed = patientAppts.filter(a => a.status === 'completada').sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
    return {
      next: upcoming[0] ?? null,
      lastVisit: completed[0] ?? null,
      total: patientAppts.length
    };
  }, [patientAppts]);

  // Respuestas rápidas con la info de clínica configurada.
  const quickReplies = useMemo(() => buildQuickReplies(clinicInfo), [clinicInfo]);

  // Cargar info de clínica desde app_settings.
  const loadClinicInfo = useCallback(async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('app_settings').select('value').eq('key', CLINIC_INFO_KEY).maybeSingle();
      if (error) return;
      if (data?.value) {
        const v = data.value as Partial<ClinicInfo>;
        setClinicInfo({
          hours: v.hours ?? '',
          address: v.address ?? '',
          maps_url: v.maps_url ?? '',
          phone: v.phone ?? '',
          payment_methods: v.payment_methods ?? '',
          faq: Array.isArray(v.faq) ? v.faq : []
        });
      }
    } catch {

      // app_settings puede no existir aún; el bot opera sin info.
    }
  }, []);
  useEffect(() => {
    loadClinicInfo();
  }, [loadClinicInfo]);
  const saveClinicInfo = async () => {
    setSavingClinic(true);
    setClinicSaved(false);
    try {
      const {
        error
      } = await supabase.from('app_settings').upsert({
        key: CLINIC_INFO_KEY,
        value: clinicInfo,
        updated_at: new Date().toISOString(),
        updated_by: user?.name || user?.email || 'Agente'
      }, {
        onConflict: 'key'
      });
      if (error) throw error;
      setClinicSaved(true);
      setTimeout(() => setClinicSaved(false), 2500);
    } catch (err) {
      notifyRef.current?.({
        type: 'system' as const,
        title: 'Error guardando la información de la clínica',
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setSavingClinic(false);
    }
  };

  // Stats / contadores de la bandeja
  const counts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      pending: conversations.filter(c => statusOf(c) === 'pending').length,
      mine: conversations.filter(c => statusOf(c) === 'assigned' && c.assigned_to === user?.id).length,
      bot: conversations.filter(c => statusOf(c) === 'bot').length,
      resolved: conversations.filter(c => statusOf(c) === 'resolved').length,
      total: conversations.length,
      sentToday: outreach.filter(o => o.status === 'sent' && o.sent_at?.startsWith(today)).length,
      failedToday: outreach.filter(o => o.status === 'failed' && (o.sent_at?.startsWith(today) || o.created_at.startsWith(today))).length
    };
  }, [conversations, outreach, user]);
  const runSchedulerJob = async (job: string) => {
    setRunningJob(job);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      // @ts-expect-error supabaseUrl es interno pero estable
      const baseUrl: string = supabase.supabaseUrl;
      // @ts-expect-error supabaseKey es interno pero estable
      const anonKey: string = supabase.supabaseKey;
      const url = `${baseUrl}/functions/v1/bot-scheduler?job=${encodeURIComponent(job)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token ?? anonKey}`,
          apikey: anonKey
        },
        body: '{}'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      notifyRef.current?.({
        type: 'system' as const,
        title: `Job ${job} ejecutado`,
        message: `Enviados: ${data?.total_sent ?? 0} · Fallidos: ${data?.total_failed ?? 0}`
      });
      refreshAll();
    } catch (err) {
      notifyRef.current?.({
        type: 'system' as const,
        title: `Error ejecutando ${job}`,
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setRunningJob(null);
    }
  };
  const selectedConv = conversations.find(c => c.id === selectedConvId) || null;
  const selectedStatus = selectedConv ? statusOf(selectedConv) : 'bot';
  const sendManualMessage = async () => {
    if (!selectedConvId || !draft.trim() || sending) return;
    setSending(true);
    try {
      // @ts-expect-error supabaseUrl es interno pero estable
      const baseUrl: string = supabase.supabaseUrl;
      // @ts-expect-error supabaseKey es interno pero estable
      const anonKey: string = supabase.supabaseKey;
      const url = `${baseUrl}/functions/v1/bot-send`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${anonKey}`,
          apikey: anonKey
        },
        body: JSON.stringify({
          conversation_id: selectedConvId,
          body: draft.trim(),
          sent_by: user?.name || user?.email || 'Agente'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setDraft('');
      loadMessages(selectedConvId);
    } catch (err) {
      notifyRef.current?.({
        type: 'system' as const,
        title: 'Error enviando mensaje',
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setSending(false);
    }
  };

  // Cambia el estado del flujo de la conversación abierta (asignar / resolver /
  // devolver al bot). Mantiene bot_paused en sintonía con status.
  const changeStatus = async (status: ConvStatus, assignee?: {
    id: string;
    name: string;
  }) => {
    if (!selectedConv || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const patch: Record<string, unknown> = {
        status,
        bot_paused: status !== 'bot'
      };
      if (status === 'assigned' && assignee) {
        patch.assigned_to = assignee.id;
        patch.assigned_to_name = assignee.name;
        patch.assigned_at = new Date().toISOString();
      }
      if (status === 'bot') {
        patch.assigned_to = null;
        patch.assigned_to_name = null;
        patch.assigned_at = null;
      }
      if (status === 'resolved') {
        patch.resolved_at = new Date().toISOString();
      }
      const {
        error
      } = await supabase.from('bot_conversations').update(patch).eq('id', selectedConv.id);
      if (error) throw error;
      setAssignMenuOpen(false);
      refreshAll();
    } catch (err) {
      notifyRef.current?.({
        type: 'system' as const,
        title: 'Error cambiando estado de la conversación',
        message: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setUpdatingStatus(false);
    }
  };
  const takeConversation = () => {
    if (!user) return;
    changeStatus('assigned', {
      id: user.id,
      name: user.name || user.email
    });
  };
  const isWindowExpired = selectedConv && !isWindowOpen(selectedConv.window_expires_at);
  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (listFilter === 'pending') list = list.filter(c => statusOf(c) === 'pending');else if (listFilter === 'mine') list = list.filter(c => statusOf(c) === 'assigned' && c.assigned_to === user?.id);else if (listFilter === 'bot') list = list.filter(c => statusOf(c) === 'bot');else if (listFilter === 'resolved') list = list.filter(c => statusOf(c) === 'resolved');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => {
        const name = (c.patient?.name || '').toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        const senderId = c.sender_id.toLowerCase();
        return name.includes(q) || phone.includes(q) || senderId.includes(q);
      });
    }
    return list;
  }, [conversations, listFilter, search, user]);

  // ----- Chips de filtro de la lista -----
  const filterChips: {
    id: ListFilter;
    label: string;
    count?: number;
  }[] = [{
    id: 'all',
    label: 'Todos',
    count: counts.total
  }, {
    id: 'pending',
    label: 'Pendientes',
    count: counts.pending
  }, {
    id: 'mine',
    label: 'Míos',
    count: counts.mine
  }, {
    id: 'bot',
    label: 'Bot',
    count: counts.bot
  }, {
    id: 'resolved',
    label: 'Resueltos',
    count: counts.resolved
  }];
  function renderStatusBadge(c: BotConversation) {
    const s = statusOf(c);
    if (s === 'pending') return <Badge variant="warning" dot data-id="element-1160" data-mp="1ai582hpv0eox">Pendiente</Badge>;
    if (s === 'assigned') return <Badge variant="info" dot data-id="element-1161" data-mp="1fhrlos9lnwy9">{c.assigned_to_name || 'Asignado'}</Badge>;
    if (s === 'resolved') return <Badge variant="success" dot data-id="element-1162" data-mp="1kgky0hd5hhwu">Resuelto</Badge>;
    return <Badge variant="default" data-id="element-1163" data-mp="1ki5s65vjyyu6">Bot</Badge>;
  }

  // Punto de color compacto por estado, para la fila del lateral.
  function renderStatusDot(c: BotConversation) {
    const s = statusOf(c);
    const color = s === 'pending' ? 'bg-amber-400' : s === 'assigned' ? 'bg-cyan-500' : s === 'resolved' ? 'bg-emerald-500' : 'bg-gray-300';
    const label = s === 'pending' ? 'Pendiente' : s === 'assigned' ? c.assigned_to_name || 'Asignado' : s === 'resolved' ? 'Resuelto' : 'Bot';
    return <span className={`w-1.5 h-1.5 rounded-full ${color}`} title={label} data-id="element-1164" data-mp="287qbwqzj604i" />;
  }

  // Portal de respuestas rápidas — escapa el overflow:hidden del Card
  const quickRepliesPortal = quickRepliesOpen && quickRepliesPos ? createPortal(<div ref={quickRepliesMenuRef} style={{
    position: 'fixed',
    bottom: quickRepliesPos.bottom,
    left: quickRepliesPos.left,
    zIndex: 9999
  }} className="w-72 max-h-64 overflow-y-auto rounded-xl border border-gray-100 dark:border-white/10 bg-white dark:bg-[#1c2333] shadow-2xl py-1" data-id="element-1165" data-mp="lzxxlyfd2i1">
      {quickReplies.map(qr => <button key={qr.label} onClick={() => {
      setDraft(d => d ? d + '\n' + qr.text : qr.text);
      setQuickRepliesOpen(false);
    }} className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/[0.05]" data-id="element-1166" data-mp="k8o793n0ndzi">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-100" data-id="element-1167" data-mp="ai30uu13p561">{qr.label}</p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate" data-id="element-1168" data-mp="5kjoy0rvl4h9">{qr.text}</p>
        </button>)}
    </div>, document.body) : null;

  // Portal del menú "Asignar" — escapa el overflow:hidden del Card
  const assignPortal = assignMenuOpen && assignMenuPos ? createPortal(<div ref={assignMenuRef} style={{
    position: 'fixed',
    top: assignMenuPos.top,
    right: assignMenuPos.right,
    zIndex: 9999
  }} className="w-56 max-h-72 overflow-y-auto rounded-xl border border-gray-100 dark:border-white/10 bg-white dark:bg-[#1c2333] shadow-2xl py-1" data-id="element-1169" data-mp="2sg9no3qnkgal">
      <p className="px-3 py-1.5 text-[10px] uppercase font-semibold text-gray-400" data-id="element-1170" data-mp="2ionaw2yo295q">Asignar a</p>
      {users.length === 0 && <p className="px-3 py-2 text-xs text-gray-400" data-id="element-1171" data-mp="28sjxu22a0q40">Sin usuarios cargados</p>}
      {users.map(u => <button key={u.id} onClick={() => {
      setAssignMenuOpen(false);
      changeStatus('assigned', {
        id: u.id,
        name: u.name || u.email
      });
    }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] flex items-center gap-2" data-id="element-1172" data-mp="2dr52cdok9vy9">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-1173" data-mp="3u5n1mu5d8fxn">
            {initials(u.name || u.email)}
          </div>
          <span className="truncate" data-id="element-1174" data-mp="d4bswskpusgr">{u.name || u.email}</span>
          {selectedConv?.assigned_to === u.id && <CheckCircleIcon className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" data-id="element-1175" data-mp="858nh70tqk89" />}
        </button>)}
    </div>, document.body) : null;

  // Portal del menú "más acciones" (móvil) — mismas acciones que la barra de escritorio
  const morePortal = moreMenuOpen && moreMenuPos && selectedConv ? createPortal(<div ref={moreMenuRef} style={{
    position: 'fixed',
    top: moreMenuPos.top,
    right: moreMenuPos.right,
    zIndex: 9999
  }} className="w-52 overflow-hidden rounded-xl border border-gray-100 dark:border-white/10 bg-white dark:bg-[#1c2333] shadow-2xl py-1" data-id="element-1176" data-mp="vsgrzmqfhp98">
      <button onClick={() => {
      setMoreMenuOpen(false);
      setShowInfo(s => !s);
    }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] flex items-center gap-2.5 text-gray-700 dark:text-gray-200" data-id="element-1177" data-mp="10sbktvtrkowx">
        <UserIcon className="w-4 h-4 flex-shrink-0 text-gray-400" data-id="element-1178" data-mp="32m11ggawcaxl" /> Ver ficha del paciente
      </button>
      {(selectedStatus !== 'assigned' || selectedConv.assigned_to !== user?.id) && <button onClick={() => {
      setMoreMenuOpen(false);
      takeConversation();
    }} disabled={updatingStatus} className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] flex items-center gap-2.5 text-cyan-700 dark:text-cyan-300 disabled:opacity-50" data-id="element-1179" data-mp="38z5du8dyizst">
          <HandIcon className="w-4 h-4 flex-shrink-0" data-id="element-1180" data-mp="2lc6un2ygddr7" /> Tomar yo
        </button>}
      <button ref={assignBtnRef} onClick={() => {
      const rect = moreMenuRef.current?.getBoundingClientRect();
      setMoreMenuOpen(false);
      if (rect) {
        setAssignMenuPos({
          top: rect.top,
          right: window.innerWidth - rect.right
        });
      }
      setAssignMenuOpen(true);
    }} disabled={updatingStatus} className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] flex items-center gap-2.5 text-gray-700 dark:text-gray-200 disabled:opacity-50" data-id="element-1181" data-mp="2pbpk8c17yd0d">
        <UserPlusIcon className="w-4 h-4 flex-shrink-0 text-gray-400" data-id="element-1182" data-mp="1z15gtsroxlf7" /> Asignar a un agente
      </button>
      {selectedStatus !== 'resolved' && <button onClick={() => {
      setMoreMenuOpen(false);
      changeStatus('resolved');
    }} disabled={updatingStatus} className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] flex items-center gap-2.5 text-emerald-700 dark:text-emerald-300 disabled:opacity-50" data-id="element-1183" data-mp="1p4am7kyjg567">
          <CheckCircleIcon className="w-4 h-4 flex-shrink-0" data-id="element-1184" data-mp="1bcw0359c3nux" /> Marcar como resuelto
        </button>}
      {selectedStatus !== 'bot' && <button onClick={() => {
      setMoreMenuOpen(false);
      changeStatus('bot');
    }} disabled={updatingStatus} className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] flex items-center gap-2.5 text-gray-500 dark:text-gray-400 disabled:opacity-50" data-id="element-1185" data-mp="16d18t0lncw9c">
          <BotIcon className="w-4 h-4 flex-shrink-0" data-id="element-1186" data-mp="sdrwic0kaman" /> Devolver al bot
        </button>}
    </div>, document.body) : null;
  // La alerta de escaladas vive en el hero (badge) y en la tarjeta KPI "Esperando
  // asesor"; aquí solo va la rejilla lista|chat para no robar altura al chat.
  const conversationsView = <div className="grid grid-cols-1 lg:grid-cols-4 gap-3" data-id="element-1187" data-mp="8t9ubr47tx8q">
      {/* Lista */}
      <Card padding="none" className={`lg:col-span-1 h-[calc(100dvh-300px)] min-h-[360px] sm:h-[calc(100vh-300px)] sm:min-h-[420px] lg:h-[calc(100vh-340px)] flex flex-col ${selectedConvId ? 'hidden lg:flex' : 'flex'}`} data-id="element-1188" data-mp="dqt86px04805">
        {/* Buscador */}
        <div className="px-3 pt-3 pb-2 border-b border-gray-100 dark:border-white/[0.06] flex-shrink-0 space-y-2" data-id="element-1189" data-mp="16f7675o3b4bp">
          <div className="relative" data-id="element-1190" data-mp="roztz7ooehjg">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o teléfono..." className="w-full pl-8 pr-3 py-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-transparent focus:bg-white dark:focus:bg-white/[0.07] focus:border-cyan-500/40 focus:outline-none text-sm placeholder:text-gray-400 transition-colors" data-id="element-1191" data-mp="wpnjfox9aqbk" />

            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1192" data-mp="34xtx882xtuyc">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M16.5 10.5a6 6 0 11-12 0 6 6 0 0112 0z" data-id="element-1193" data-mp="3inxdmif4yufv" />
            </svg>
          </div>
          {/* Chips de filtro */}
          <div className="flex items-center gap-1 overflow-x-auto pb-0.5" data-id="element-1194" data-mp="2qykmvp1exv2a">
            {filterChips.map(chip => <button key={chip.id} onClick={() => setListFilter(chip.id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${listFilter === chip.id ? 'bg-cyan-500 text-white' : 'bg-gray-100 dark:bg-white/[0.05] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/[0.08]'}`} data-id="element-1195" data-mp="2m0snk9yfv6o2">

                {chip.label}
                {chip.count !== undefined && chip.count > 0 && <span className={`px-1.5 rounded-full text-[10px] ${chip.id === 'pending' && listFilter !== 'pending' ? 'bg-amber-400 text-amber-950' : listFilter === chip.id ? 'bg-white/25' : 'bg-gray-300/70 dark:bg-white/10'}`} data-id="element-1196" data-mp="ubry46b9fz0a">
                    {chip.count}
                  </span>}
              </button>)}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-id="element-1197" data-mp="183pynsqwomku">
        <div className="divide-y divide-gray-100 dark:divide-white/[0.05]" data-id="element-1198" data-mp="1d1zu84gjlp6z">
          {filteredConversations.length === 0 && !loading && <div className="px-4 py-10 text-center text-sm text-gray-400" data-id="element-1199" data-mp="1i0b3oe2xdxoo">
              {search.trim() || listFilter !== 'all' ? 'Sin resultados en este filtro' : 'Sin conversaciones todavía'}
            </div>}
          {filteredConversations.map(c => {
            const isActive = c.id === selectedConvId;
            const winOpen = isWindowOpen(c.window_expires_at);
            const displayName = c.patient?.name || c.phone || c.sender_id;
            const preview = messagePreview(c.lastMessage);
            const previewIsOut = c.lastMessage?.direction === 'out';
            const s = statusOf(c);
            const unread = isUnread(c);
            const isFreshEscalation = s === 'pending' && freshEscalations.has(c.id);
            return <button key={c.id} onClick={() => setSelectedConvId(c.id)} className={`w-full text-left px-2.5 py-2 transition-colors flex items-center gap-2.5 border-l-2 ${isActive ? 'bg-cyan-50/60 dark:bg-cyan-500/[0.08] border-cyan-500' : isFreshEscalation ? 'bg-amber-50 dark:bg-amber-500/[0.10] hover:bg-amber-100/70 dark:hover:bg-amber-500/[0.15] border-amber-400' : s === 'pending' ? 'bg-amber-50/40 dark:bg-amber-500/[0.05] hover:bg-amber-50 dark:hover:bg-amber-500/[0.08] border-amber-300/70' : unread ? 'bg-cyan-50/30 dark:bg-cyan-500/[0.04] hover:bg-gray-50 dark:hover:bg-white/[0.03] border-transparent' : 'hover:bg-gray-50 dark:hover:bg-white/[0.03] border-transparent'}`} data-id="element-1200" data-mp="3bhqkzbw2lf20">

                {/* Avatar */}
                <div className="relative flex-shrink-0" data-id="element-1201" data-mp="1c4eplxcux5kj">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 text-white text-[11px] font-bold flex items-center justify-center" data-id="element-1202" data-mp="1c57xj1njy149">
                    {initials(displayName)}
                  </div>
                  {s === 'pending' && <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center" title="Pendiente: el bot escaló a un asesor" data-id="element-1203" data-mp="1h3rlt8pmbozs">
                      {isFreshEscalation && <span className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping" data-id="element-1204" data-mp="1vuidmgr7mo2w" />}
                      <span className="relative w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-white dark:border-[#111827]" data-id="element-1205" data-mp="1qvnjwjrpff40" />
                    </span>}
                </div>
                {/* Contenido */}
                <div className="flex-1 min-w-0" data-id="element-1206" data-mp="aet5dxwj0yv9">
                  <div className="flex items-center justify-between gap-2" data-id="element-1207" data-mp="afl0z8q4iwlj">
                    <p className={`text-[13px] truncate ${unread ? 'font-bold text-gray-900 dark:text-white' : 'font-semibold text-gray-800 dark:text-gray-100'}`} data-id="element-1208" data-mp="5h1e963phgs4">
                      {displayName}
                    </p>
                    <span className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1209" data-mp="2m1wotbwtbxo4">
                      {s !== 'bot' && renderStatusDot(c)}
                      <span className={`text-[10px] whitespace-nowrap ${unread ? 'text-cyan-600 dark:text-cyan-400 font-semibold' : 'text-gray-400'}`} data-id="element-1210" data-mp="2cclg3qdu8v0r">
                        {timeAgo(c.last_inbound_at)}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5" data-id="element-1211" data-mp="239u5ttwp85ap">
                    <p className={`text-[11px] truncate flex-1 ${unread ? 'text-gray-700 dark:text-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1212" data-mp="1pdgtt0axow3m">
                      {previewIsOut && <span className="text-cyan-600 dark:text-cyan-400 mr-0.5" data-id="element-1213" data-mp="3jr83fzt9jfz2">✓</span>}
                      {preview}
                    </p>
                    {unread && <span className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" title="Sin leer" data-id="element-1214" data-mp="3npfjstjtef9q" />}
                  </div>
                </div>
              </button>;
          })}
        </div>
        </div>
      </Card>

      {/* Detalle */}
      <Card padding="none" className={`lg:col-span-3 ${selectedConvId ? 'h-[calc(100dvh-160px-56px)] md:h-[calc(100dvh-160px)] min-h-0 mb-[calc(56px+env(safe-area-inset-bottom))] md:mb-0' : 'h-[calc(100vh-220px)] min-h-[420px]'} sm:h-[calc(100vh-300px)] sm:min-h-[420px] lg:h-[calc(100vh-340px)] flex flex-col ${selectedConvId ? 'flex' : 'hidden lg:flex'}`} data-id="element-1215" data-mp="rjo0guv3gkrp">
        <CardHeader className="px-4 py-3" data-id="element-1216" data-mp="1ahihnw6dm2l6">
          {selectedConv ? <div className="flex items-center justify-between gap-2 flex-nowrap" data-id="element-1217" data-mp="1afxnw8rvynod">
              <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-1218" data-mp="15hchtspyd9s0">
                <button onClick={() => setSelectedConvId(null)} className="lg:hidden flex items-center justify-center w-8 h-8 -ml-1 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.08] transition-colors flex-shrink-0" title="Volver a la lista" data-id="element-1219" data-mp="22ybatdboop8o">
                  <ArrowLeftIcon className="w-5 h-5" data-id="element-1220" data-mp="2ahauzl6yykt3" />
                </button>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-1221" data-mp="2ajewjwheb7q7">
                  {initials(selectedConv.patient?.name || selectedConv.phone || selectedConv.sender_id)}
                </div>
                <div className="min-w-0" data-id="element-1222" data-mp="2oezbyhxx17sv">
                  <p className="text-base font-semibold text-gray-900 dark:text-white truncate" data-id="element-1223" data-mp="2tfer9f1rcpdh">
                    {selectedConv.patient?.name || selectedConv.phone || 'Conversación'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-1224" data-mp="334y8yhdqid6r">
                    {channelLabel(selectedConv.channel)} · {selectedConv.phone || selectedConv.sender_id}
                  </p>
                </div>
              </div>
              {/* Acciones — en pantallas anchas se ven todas; en móvil van dentro del menú ⋯ */}
              <div className="flex items-center gap-1 flex-shrink-0 flex-nowrap" data-id="element-1225" data-mp="2yhkwtw9p408j">
                <button title="Buscar en la conversación" onClick={() => {
              setShowChatSearch(s => !s);
              if (showChatSearch) setChatSearch('');
            }} className={`flex items-center justify-center w-8 h-8 rounded-lg text-xs font-medium transition-colors ${showChatSearch ? 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' : 'bg-gray-100 dark:bg-white/[0.08] hover:bg-gray-200 dark:hover:bg-white/[0.13] text-gray-700 dark:text-gray-200'}`} data-id="element-1226" data-mp="2ui3sj660994y">
                  <SearchIcon className="w-3.5 h-3.5" data-id="element-1227" data-mp="2pkkd42xkmqbf" />
                </button>
                <button title="Ver ficha del paciente" onClick={() => setShowInfo(s => !s)} className={`hidden sm:flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${showInfo ? 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' : 'bg-gray-100 dark:bg-white/[0.08] hover:bg-gray-200 dark:hover:bg-white/[0.13] text-gray-700 dark:text-gray-200'}`} data-id="element-1228" data-mp="2kjeks67tp52f">
                  <UserIcon className="w-3.5 h-3.5" data-id="element-1229" data-mp="1mrg7b0oedvnq" />
                  <span className="hidden md:inline" data-id="element-1230" data-mp="18r5fnvf9kd1i">Ficha</span>
                </button>
                {(selectedStatus !== 'assigned' || selectedConv.assigned_to !== user?.id) && <button title="Tomar yo" onClick={takeConversation} disabled={updatingStatus} className="hidden sm:flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50 transition-colors" data-id="element-1231" data-mp="1dq8jjcjicd7w">
                    <HandIcon className="w-3.5 h-3.5" data-id="element-1232" data-mp="1roou5ekhqnvb" />
                    <span className="hidden md:inline" data-id="element-1233" data-mp="1woupb4pyysi5">Tomar yo</span>
                  </button>}

                {/* Asignar — dropdown via portal para escapar overflow:hidden del Card */}
                <div className="relative hidden sm:block" data-id="element-1234" data-mp="2kckls4v7dpl8">
                  <button ref={assignBtnRef} title="Asignar a un agente" onClick={() => {
                const rect = assignBtnRef.current?.getBoundingClientRect();
                if (rect) {
                  setAssignMenuPos({
                    top: rect.bottom + 4,
                    right: window.innerWidth - rect.right
                  });
                }
                setAssignMenuOpen(o => !o);
              }} disabled={updatingStatus} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-white/[0.08] hover:bg-gray-200 dark:hover:bg-white/[0.13] text-gray-700 dark:text-gray-200 disabled:opacity-50 transition-colors" data-id="element-1235" data-mp="2p5xasg3snyei">
                    <UserPlusIcon className="w-3.5 h-3.5" data-id="element-1236" data-mp="26di46h75cyay" />
                    <span className="hidden md:inline" data-id="element-1237" data-mp="21ddmwoyezzjo">Asignar</span>
                    <ChevronDownIcon className="w-3 h-3" data-id="element-1238" data-mp="21f6se0xakdq3" />
                  </button>
                </div>

                {selectedStatus !== 'resolved' && <button title="Marcar como resuelto" onClick={() => changeStatus('resolved')} disabled={updatingStatus} className="hidden sm:flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50 transition-colors" data-id="element-1239" data-mp="1scxmjocyx8j1">
                    <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1240" data-mp="gvkwvk4ac7lo" />
                    <span className="hidden md:inline" data-id="element-1241" data-mp="gw4hj8lbla3e">Resolver</span>
                  </button>}
                {selectedStatus !== 'bot' && <button title="Devolver al bot" onClick={() => changeStatus('bot')} disabled={updatingStatus} className="hidden sm:flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] disabled:opacity-50 transition-colors" data-id="element-1242" data-mp="3kc1shjrb2m2s">
                    <BotIcon className="w-3.5 h-3.5" data-id="element-1243" data-mp="2w21zt0hisrb" />
                  </button>}

                {/* Botón ⋯ — solo en móvil, abre el menú con todas las acciones */}
                <button ref={moreBtnRef} title="Más acciones" onClick={() => {
              const rect = moreBtnRef.current?.getBoundingClientRect();
              if (rect) {
                setMoreMenuPos({
                  top: rect.bottom + 4,
                  right: window.innerWidth - rect.right
                });
              }
              setMoreMenuOpen(o => !o);
            }} className={`sm:hidden flex items-center justify-center w-8 h-8 rounded-lg text-xs font-medium transition-colors ${moreMenuOpen ? 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' : 'bg-gray-100 dark:bg-white/[0.08] hover:bg-gray-200 dark:hover:bg-white/[0.13] text-gray-700 dark:text-gray-200'}`} data-id="element-1244" data-mp="31jpqiy4kdmt6">
                  <MoreVerticalIcon className="w-4 h-4" data-id="element-1245" data-mp="2roh4fmt2p9so" />
                </button>
              </div>
            </div> : <CardTitle data-id="element-1246" data-mp="2nn5gdlpukwml">Selecciona una conversación</CardTitle>}
        </CardHeader>

        {/* Barra de búsqueda dentro del chat */}
        {selectedConv && showChatSearch && <div className="px-4 py-2 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] flex items-center gap-2" data-id="element-1247" data-mp="31kk9b0fb7mjb">
            <SearchIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-1248" data-mp="3g93rsb19vhr5" />
            <input autoFocus type="text" value={chatSearch} onChange={e => setChatSearch(e.target.value)} placeholder="Buscar en esta conversación…" className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-gray-400 text-gray-900 dark:text-gray-100" data-id="element-1249" data-mp="3l8gbimwuk8pi" />
            {chatSearch && <button onClick={() => setChatSearch('')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" data-id="element-1250" data-mp="3bc3mw3g5sl1m">
                <XIcon className="w-4 h-4" data-id="element-1251" data-mp="32a44p7terax3" />
              </button>}
          </div>}

        {/* Banner de estado */}
        {selectedConv && selectedStatus === 'pending' && <div className="px-4 py-3 border-t-2 border-amber-300 dark:border-amber-500/40 bg-gradient-to-r from-amber-50 to-orange-50/50 dark:from-amber-500/[0.14] dark:to-orange-500/[0.06] flex items-center gap-3" data-id="element-1252" data-mp="2nilhoq24sg48">
            <span className="relative flex-shrink-0" data-id="element-1253" data-mp="2illimz3p8dmm">
              <span className="absolute inset-0 rounded-full bg-amber-400/40 animate-ping" data-id="element-1254" data-mp="2il3ezhepix4o" />
              <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 text-amber-950" data-id="element-1255" data-mp="18ructuq0ckj8">
                <HandIcon className="w-4 h-4" data-id="element-1256" data-mp="1dpm20gz9psjq" />
              </span>
            </span>
            <div className="min-w-0 flex-1" data-id="element-1257" data-mp="ztif78qq4mha">
              <p className="text-sm font-bold text-amber-900 dark:text-amber-200" data-id="element-1258" data-mp="13t993lbjriq1">El bot pasó esta conversación a un asesor</p>
              <p className="text-xs text-amber-700/90 dark:text-amber-300/80" data-id="element-1259" data-mp="1rk4xe8gsnvsj">El cliente espera atención humana. Tómala tú o asígnala a un compañero.</p>
            </div>
            <button onClick={takeConversation} disabled={updatingStatus} className="flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 transition-colors" data-id="element-1260" data-mp="1ir1mo7xj9x1l">
              <HandIcon className="w-3.5 h-3.5" data-id="element-1261" data-mp="2agelzppbo4qs" />
              Tomar yo
            </button>
          </div>}
        {selectedConv && selectedStatus === 'assigned' && <div className="px-4 py-2 bg-cyan-50 dark:bg-cyan-500/10 border-t border-cyan-100 dark:border-cyan-500/20 flex items-center gap-2 text-xs text-cyan-700 dark:text-cyan-300" data-id="element-1262" data-mp="3t75s8pwstcf">
            <HandIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1263" data-mp="djik8p77ykxk" />
            <span data-id="element-1264" data-mp="diqq7it9sv9e">Atendida por {selectedConv.assigned_to_name || 'un agente'}. El bot está en pausa para esta conversación.</span>
          </div>}
        {selectedConv && selectedStatus === 'resolved' && <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-500/10 border-t border-emerald-100 dark:border-emerald-500/20 flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300" data-id="element-1265" data-mp="3h0r0j4matcq6">
            <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1266" data-mp="3qzzptk67y367" />
            <span data-id="element-1267" data-mp="3lz297c0uxh6t">Conversación resuelta. Si el cliente vuelve a escribir, el bot la atiende de nuevo.</span>
          </div>}

        <div className="relative flex-1 min-h-0 flex" data-id="element-1268" data-mp="166mjedjms51f">
        {!selectedConv ? <div className="flex-1 flex flex-col items-center justify-center text-center px-6 bg-gray-50/40 dark:bg-black/10" data-id="element-1269" data-mp="119w3prbqnbgj">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/15 to-teal-500/10 flex items-center justify-center mb-3" data-id="element-1270" data-mp="18brl2o0u6y1h">
              <MessageSquareIcon className="w-7 h-7 text-cyan-500" data-id="element-1271" data-mp="1iah44k4gyojb" />
            </div>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200" data-id="element-1272" data-mp="1i8mi12hgoqdn">Selecciona una conversación</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-xs" data-id="element-1273" data-mp="pltfcekihh10">Elige un chat de la lista para ver los mensajes y responder.</p>
          </div> : <div ref={messagesContainerRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5 bg-[#efeae2] dark:bg-[#0b141a] bg-[radial-gradient(rgba(0,0,0,0.025)_1px,transparent_1px)] dark:bg-[radial-gradient(rgba(255,255,255,0.02)_1px,transparent_1px)] [background-size:18px_18px]" data-id="element-1274" data-mp="ulo1k4tcdifq">
          {loadingMessages && <div className="flex flex-col items-center justify-center py-12 text-gray-400" data-id="element-1275" data-mp="6tha9k2h41n6">
              <RefreshCwIcon className="w-5 h-5 animate-spin mb-2" data-id="element-1276" data-mp="bs22aw7knf0j" />
              <p className="text-xs" data-id="element-1277" data-mp="2p44u7pd4asbq">Cargando mensajes…</p>
            </div>}
          {!loadingMessages && messages.length === 0 && selectedConv && <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500" data-id="element-1278" data-mp="2g3zxqa9gpecc">
              <MessageSquareIcon className="w-8 h-8 mb-2 opacity-40" data-id="element-1279" data-mp="2b55g3bn8dvtt" />
              <p className="text-xs" data-id="element-1280" data-mp="2b6zqvx4sg7k6">Aún no hay mensajes en esta conversación</p>
            </div>}
          {(() => {
            const q = chatSearch.trim().toLowerCase();
            const shown = q ? messages.filter(m => (m.body ?? '').toLowerCase().includes(q)) : messages;
            if (q && shown.length === 0) {
              return <p className="text-center text-xs text-gray-400 py-6" data-id="element-1281" data-mp="1sexj7ba4k1m2">Sin coincidencias para "{chatSearch}"</p>;
            }
            let lastDay = '';
            return shown.map(m => {
              const isOut = m.direction === 'out';
              const fallback = (m.payload as {
                ai_fallback?: boolean;
              })?.ai_fallback === true;
              const manual = (m.payload as {
                manual?: boolean;
              })?.manual === true || !!m.sent_by;
              const isMedia = m.type === 'media' || m.body === '[media]';
              const isAudioTranscribed = m.type === 'audio_transcribed';
              const imgUrl = publicImageUrl(m);
              const day = dayLabel(m.created_at);
              const showDivider = day && day !== lastDay;
              if (showDivider) lastDay = day;
              return <React.Fragment key={m.id} data-id="element-1282" data-mp="34sdw2z4pbq8t">
                {showDivider && <div className="flex justify-center my-3" data-id="element-1283" data-mp="2pulhc96zb5vo">
                    <span className="px-3 py-1 rounded-full bg-white/80 dark:bg-white/10 text-[11px] font-medium text-gray-500 dark:text-gray-300 shadow-sm" data-id="element-1284" data-mp="2pw67znq6a4o9">
                      {day}
                    </span>
                  </div>}
                <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`} data-id="element-1285" data-mp="t1s1vuitcrwq">
                  <div className={`relative max-w-[78%] min-w-0 rounded-2xl px-3 py-2 text-sm shadow-sm ${isOut ? manual ? 'bg-violet-500 text-white rounded-br-md' : 'bg-[#d9fdd3] dark:bg-[#005c4b] text-gray-900 dark:text-gray-50 rounded-br-md' : 'bg-white dark:bg-[#202c33] text-gray-800 dark:text-gray-100 rounded-bl-md'}`} data-id="element-1286" data-mp="2dj16v9rzxfch">

                    {isAudioTranscribed && <p className={`text-[10px] uppercase font-semibold mb-1 ${isOut ? 'opacity-70' : 'text-cyan-600 dark:text-cyan-400'}`} data-id="element-1287" data-mp="2ihcfjh6k1jt4">
                        🎙️ Nota de voz
                      </p>}
                    {imgUrl ? <img src={imgUrl} alt="imagen" onClick={() => setLightboxUrl(imgUrl)} className="rounded-lg mb-1 max-h-56 w-auto cursor-zoom-in object-cover" data-id="element-1288" data-mp="2sata4nein2xu" /> : isMedia ? <p className={`italic flex items-center gap-1 ${isOut ? 'opacity-90' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1289" data-mp="31a6itdutfnhi">
                        🖼️ Imagen recibida (ábrela en WhatsApp)
                      </p> : null}
                    {m.body && m.body !== '[media]' && <p className="whitespace-pre-wrap break-words leading-snug [&_a]:break-all" dangerouslySetInnerHTML={{
                      __html: formatWhatsApp(m.body)
                    }} data-id="element-1290" data-mp="36xn13djznjds" />}
                    <div className={`flex items-center justify-end gap-1.5 mt-0.5 text-[10px] ${isOut ? 'opacity-70' : 'text-gray-400'}`} data-id="element-1291" data-mp="3fybgabl4bps9">
                      {isOut && manual && m.sent_by && <span className="font-medium truncate max-w-[100px]" data-id="element-1292" data-mp="3kus4f5sbi45c">{m.sent_by}</span>}
                      {isOut && !manual && <span className="font-medium" data-id="element-1293" data-mp="3uvc9ykmvdutd">bot</span>}
                      {fallback && <span className="px-1 rounded bg-amber-200/90 text-amber-900 font-medium" data-id="element-1294" data-mp="3ut7x3d6ztlgy">fallback</span>}
                      {m.status === 'failed' && <span className="px-1 rounded bg-red-200/90 text-red-900 font-medium" data-id="element-1295" data-mp="7irshs37uzkl">falló</span>}
                      <span data-id="element-1296" data-mp="chuv75vq4tpe">{timeOnly(m.created_at)}</span>
                    </div>
                  </div>
                </div>
              </React.Fragment>;
            });
          })()}
          <div ref={messagesEndRef} data-id="element-1297" data-mp="rk363255ad1m" />
        </div>}

        {/* Visor de imagen (lightbox) */}
        {lightboxUrl && <div onClick={() => setLightboxUrl(null)} className="absolute inset-0 z-20 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out" data-id="element-1298" data-mp="mkqfc6b5kltr">
            <img src={lightboxUrl} alt="imagen" className="max-h-full max-w-full rounded-lg shadow-2xl" data-id="element-1299" data-mp="ho9imnpw0z52" />
            <button onClick={() => setLightboxUrl(null)} className="absolute top-3 right-3 text-white/80 hover:text-white" data-id="element-1300" data-mp="dmy1l6l2xm8h">
              <XIcon className="w-6 h-6" data-id="element-1301" data-mp="15c18ku4cpaot" />
            </button>
          </div>}

        {/* Panel de contexto del paciente (overlay) */}
        {showInfo && selectedConv && <div className="absolute inset-y-0 right-0 w-full sm:w-72 bg-white dark:bg-[#0d1117] border-l border-gray-100 dark:border-white/10 shadow-xl overflow-y-auto z-10" data-id="element-1302" data-mp="10iy9k6gt1918">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-white/[0.06] sticky top-0 bg-white dark:bg-[#0d1117]" data-id="element-1303" data-mp="1t2592sm8subl">
              <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-1304" data-mp="27y48rfx6akdv">Ficha del paciente</p>
              <button onClick={() => setShowInfo(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] text-gray-400" data-id="element-1305" data-mp="2cpvzbqgbwq40">
                <XIcon className="w-4 h-4" data-id="element-1306" data-mp="1y2ayy4au94zs" />
              </button>
            </div>
            <div className="p-3 space-y-3" data-id="element-1307" data-mp="2302rn0jb4v51">
              <div data-id="element-1308" data-mp="2qp5gotg9tq9k">
                <p className="text-base font-semibold text-gray-900 dark:text-white" data-id="element-1309" data-mp="2qolwt7728rsw">
                  {selectedConv.patient?.name || 'Sin paciente vinculado'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1310" data-mp="2gr7vyfxmeblp">
                  {channelLabel(selectedConv.channel)} · {selectedConv.phone || selectedConv.sender_id}
                </p>
              </div>

              {!selectedConv.patient_id && <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2" data-id="element-1311" data-mp="15b15c6vdjw2y">
                  Este contacto no está vinculado a un paciente del CRM. No hay historial de citas que mostrar.
                </div>}

              {selectedConv.patient_id && loadingCtx && <p className="text-xs text-gray-400 py-2" data-id="element-1312" data-mp="3jj1y8dh695vd">Cargando historial…</p>}

              {selectedConv.patient_id && !loadingCtx && <>
                  {/* Próxima cita */}
                  <div className="rounded-xl border border-gray-100 dark:border-white/[0.06] p-2.5" data-id="element-1313" data-mp="3tj2mmdtwec6f">
                    <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1 flex items-center gap-1" data-id="element-1314" data-mp="35syj06gpmcff">
                      <CalendarIcon className="w-3 h-3" data-id="element-1315" data-mp="30ux13ov0sj5j" /> Próxima cita
                    </p>
                    {patientCtx.next ? <div className="text-sm" data-id="element-1316" data-mp="3emldlt96l9n1">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-1317" data-mp="39tie74hq35yx">
                          {new Date(patientCtx.next.date + 'T00:00:00').toLocaleDateString('es-DO', {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'short'
                    })} · {patientCtx.next.time?.slice(0, 5)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1318" data-mp="1adu37jg84y7k">
                          {patientCtx.next.service?.name || 'Servicio'}{patientCtx.next.doctor?.name ? ` · ${patientCtx.next.doctor.name}` : ''}
                        </p>
                      </div> : <p className="text-xs text-gray-400" data-id="element-1319" data-mp="11bkxd6vwht0i">Sin cita programada</p>}
                  </div>

                  {/* Última visita */}
                  <div className="rounded-xl border border-gray-100 dark:border-white/[0.06] p-2.5" data-id="element-1320" data-mp="1u237grv1kunf">
                    <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1" data-id="element-1321" data-mp="1p2gtuhbaxce3">Última visita</p>
                    {patientCtx.lastVisit ? <div className="text-sm" data-id="element-1322" data-mp="1k3ne0i8jl9ar">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-1323" data-mp="dntm86m3jm0t">
                          {new Date(patientCtx.lastVisit.date + 'T00:00:00').toLocaleDateString('es-DO', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1324" data-mp="3kfvpgqajzat">{patientCtx.lastVisit.service?.name || 'Servicio'}</p>
                      </div> : <p className="text-xs text-gray-400" data-id="element-1325" data-mp="d9pokysyllzz">Sin visitas completadas</p>}
                  </div>

                  {/* Historial reciente */}
                  <div data-id="element-1326" data-mp="hbb7b0s6cui3">
                    <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1.5" data-id="element-1327" data-mp="hdxbqrjrqtw3">
                      Citas ({patientCtx.total})
                    </p>
                    <div className="space-y-1.5" data-id="element-1328" data-mp="raa7duyvjhth">
                      {patientAppts.slice(0, 8).map(a => <div key={a.id} className="flex items-center justify-between gap-2 text-xs" data-id="element-1329" data-mp="w7ir3zzo7twu">
                          <span className="text-gray-600 dark:text-gray-300 truncate" data-id="element-1330" data-mp="2t2f4dkv8d5b2">
                            {new Date(a.date + 'T00:00:00').toLocaleDateString('es-DO', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit'
                      })} · {a.service?.name || '—'}
                          </span>
                          <Badge variant={a.status === 'completada' ? 'success' : a.status === 'cancelada' ? 'danger' : a.status === 'reprogramada' ? 'warning' : 'info'} data-id="element-1331" data-mp="2zyq4itqrx63m">
                            {a.status}
                          </Badge>
                        </div>)}
                      {patientAppts.length === 0 && <p className="text-xs text-gray-400" data-id="element-1332" data-mp="33y8rdv25nr93">Sin citas registradas</p>}
                    </div>
                  </div>
                </>}
            </div>
          </div>}
        </div>

        {/* Input para responder manualmente */}
        {selectedConv && <div className="flex-shrink-0 border-t border-gray-100 dark:border-white/[0.06] p-3 bg-white dark:bg-[#111827]" data-id="element-1333" data-mp="3rouo4q6plp3s">
            {isWindowExpired ? <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2.5 flex items-center gap-2" data-id="element-1334" data-mp="3nokff5rj8kl8">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0" data-id="element-1335" data-mp="f50av7bke298" />
                <span data-id="element-1336" data-mp="f360uo1tzujx">La ventana de 24h expiró. Debes esperar a que el cliente escriba o usar una plantilla aprobada.</span>
              </div> : <div data-id="element-1337" data-mp="64bva86ivtu8">
                {/* Barra superior del composer: respuestas rápidas + emojis */}
                <div className="mb-2 flex items-center gap-2" data-id="element-1338" data-mp="3nlbv13uw0u88">
                  <button ref={quickRepliesBtnRef} onClick={() => {
              const rect = quickRepliesBtnRef.current?.getBoundingClientRect();
              if (rect) {
                setQuickRepliesPos({
                  bottom: window.innerHeight - rect.top + 4,
                  left: rect.left
                });
              }
              setQuickRepliesOpen(o => !o);
            }} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors" data-id="element-1339" data-mp="3niz5a4iw1fkm">
                    <ZapIcon className="w-3 h-3" data-id="element-1340" data-mp="brpm959ehztl" />
                    Respuestas rápidas
                    <ChevronDownIcon className="w-3 h-3" data-id="element-1341" data-mp="bu3ivijqbk39" />
                  </button>
                  <button onClick={() => setShowEmoji(s => !s)} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${showEmoji ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300' : 'bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.1]'}`} data-id="element-1342" data-mp="6ywjgmxht1k5">
                    😊 Emojis
                  </button>
                </div>
                {showEmoji && <div className="mb-2 flex flex-wrap gap-1 p-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.06]" data-id="element-1343" data-mp="3f9hn795ilu3x">
                    {['😊', '😀', '🙏', '👍', '✅', '🎉', '💆', '💅', '🦷', '✨', '📅', '⏰', '📍', '❤️', '😉', '🙌', '💯', '🔥', '👋', '😅'].map(e => <button key={e} onClick={() => setDraft(d => d + e)} className="text-lg w-8 h-8 rounded-lg hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors" data-id="element-1344" data-mp="1l1dt8hrpbsz1">
                        {e}
                      </button>)}
                  </div>}
                <div className="flex items-end gap-2" data-id="element-1345" data-mp="1usrmi7t27wjq">
                  <textarea value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
              // Solo Ctrl+Enter o Cmd+Enter envía. Enter normal salta de línea.
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                sendManualMessage();
              }
            }} placeholder={selectedStatus === 'bot' ? 'Escribe para responder manualmente (esto NO pausa al bot)…' : 'Escribe tu respuesta…'} rows={2} disabled={sending} className="flex-1 resize-none rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#0d1117] px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 disabled:opacity-60" data-id="element-1346" data-mp="1zo6xmrzonecg" />

                  <div className="flex-shrink-0" data-id="element-1347" data-mp="ndmk8wwlqmg9">
                    <Button variant="primary" size="md" onClick={sendManualMessage} disabled={!draft.trim() || sending} isLoading={sending} leftIcon={<SendIcon className="w-4 h-4" data-id="element-1349" data-mp="wdrjukvk0gjv" />} data-id="element-1348" data-mp="nexrj6p9acc4">

                      Enviar
                    </Button>
                  </div>
                </div>
                <p className="hidden sm:block text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 ml-1" data-id="element-1350" data-mp="8o6w7ooggv49">
                  {selectedStatus === 'bot' ? 'Tip: para que solo tú atiendas, usa “Tomar yo”. · Ctrl+Enter para enviar' : 'Tip: Ctrl+Enter para enviar · Enter para salto de línea'}
                </p>
              </div>}
          </div>}
      </Card>
    </div>;
  const campaignsView = <div className="space-y-4" data-id="element-1351" data-mp="3m1imfw4mopud">
      <Card data-id="element-1352" data-mp="3m0h0gwm2qixv">
        <CardHeader action={<Button variant="primary" size="sm" onClick={() => runSchedulerJob('all')} disabled={!!runningJob} leftIcon={<PlayIcon className="w-3.5 h-3.5" data-id="element-1355" data-mp="1wlg0ufkv3va8" />} data-id="element-1354" data-mp="3h4a1xow0c9py">

              {runningJob ? 'Ejecutando…' : 'Ejecutar todos los jobs'}
            </Button>} data-id="element-1353" data-mp="3h3qp2ml7sbis">

          <CardTitle subtitle="Envíos automáticos del bot por campaña" data-id="element-1356" data-mp="21eiyozwo9qw8">Campañas proactivas</CardTitle>
        </CardHeader>
        <CardContent data-id="element-1357" data-mp="1sk4csehmzi45">
          <div className="overflow-x-auto -mx-2" data-id="element-1358" data-mp="1sjbc9v99toug">
            <table className="w-full text-sm" data-id="element-1359" data-mp="1si09nyrkan4x">
              <thead data-id="element-1360" data-mp="2g9ff40emxkke">
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wide" data-id="element-1361" data-mp="2gb08hmp7qxgo">
                  <th className="px-2 py-2 font-medium" data-id="element-1362" data-mp="2ka946x3td5ev">Fecha</th>
                  <th className="px-2 py-2 font-medium" data-id="element-1363" data-mp="26brit61ds504">Paciente</th>
                  <th className="px-2 py-2 font-medium" data-id="element-1364" data-mp="26dlv5vyl6osl">Campaña</th>
                  <th className="px-2 py-2 font-medium" data-id="element-1365" data-mp="2b9awa3q98dr0">Paso</th>
                  <th className="px-2 py-2 font-medium" data-id="element-1366" data-mp="uwm6dabg6s3b">Estado</th>
                  <th className="px-2 py-2 font-medium" data-id="element-1367" data-mp="uyfcmoi5faas">Detalle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]" data-id="element-1368" data-mp="zt2sy0300yea">
                {outreach.length === 0 && <tr data-id="element-1369" data-mp="1mjxb2ta5j7mm">
                    <td colSpan={6} className="px-2 py-6 text-center text-xs text-gray-400" data-id="element-1370" data-mp="1mjnrpojm3ypw">
                      Aún no hay envíos
                    </td>
                  </tr>}
                {outreach.map(o => <tr key={o.id} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]" data-id="element-1371" data-mp="1cmj5jopro393">
                    <td className="px-2 py-2 text-xs text-gray-500 whitespace-nowrap" data-id="element-1372" data-mp="2aa1dwyfin3ck">
                      {formatDateTime(o.sent_at ?? o.created_at)}
                    </td>
                    <td className="px-2 py-2 text-gray-800 dark:text-gray-100" data-id="element-1373" data-mp="20joq8xcemu27">
                      {o.patient?.name ?? <span className="text-gray-400" data-id="element-1374" data-mp="1vkveb9crnb45">—</span>}
                    </td>
                    <td className="px-2 py-2" data-id="element-1375" data-mp="1vir9mp71m82t">
                      <Badge variant="info" data-id="element-1376" data-mp="2t113o1uu4h6w">{campaignLabel(o.campaign_type)}</Badge>
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-500" data-id="element-1377" data-mp="2o8hj2kxqclsl">step {o.step}</td>
                    <td className="px-2 py-2" data-id="element-1378" data-mp="2wiyo7rsajyot">
                      <Badge variant={outreachStatusVariant(o.status)} dot data-id="element-1379" data-mp="2wk9pnky9kucr">
                        {o.status}
                      </Badge>
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-500 max-w-[260px] truncate" title={o.error_message ?? ''} data-id="element-1380" data-mp="35htq4roaymmb">
                      {o.error_message ?? o.template_name ?? '—'}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>;
  const templatesView = <Card data-id="element-1381" data-mp="3o9cvv69poes1">
      <CardHeader data-id="element-1382" data-mp="3o8l09vg46h1r">
        <CardTitle subtitle="Plantillas registradas para envíos proactivos" data-id="element-1383" data-mp="3oa4g5kxuyq30">
          Plantillas
        </CardTitle>
      </CardHeader>
      <CardContent data-id="element-1384" data-mp="3taaaj8viiqos">
        <div className="space-y-3" data-id="element-1385" data-mp="21v56tooyown">
          {templates.length === 0 && <p className="text-sm text-gray-400 text-center py-6" data-id="element-1386" data-mp="23ppq6w1wf07">
              Sin plantillas registradas
            </p>}
          {templates.map(t => <div key={t.id} className="rounded-xl border border-gray-100 dark:border-white/[0.06] p-3.5 bg-white/60 dark:bg-white/[0.02]" data-id="element-1387" data-mp="8nccgg1ebsp7">

              <div className="flex items-center justify-between gap-3 mb-2" data-id="element-1388" data-mp="dkvuzs54mrmy">
                <div data-id="element-1389" data-mp="il0apfy7nic4">
                  <p className="font-mono text-xs font-semibold text-gray-900 dark:text-white" data-id="element-1390" data-mp="ilt8vsj3szin">{t.name}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-1391" data-mp="njutihw6h6w8">
                    {t.campaign_type ? campaignLabel(t.campaign_type) : t.category} · {channelLabel(t.channel)} · {t.language}
                  </p>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-1392" data-mp="3w06e2fjey0vr">
                  <Badge variant={t.status === 'approved' ? 'success' : t.status === 'rejected' ? 'danger' : t.status === 'paused' ? 'warning' : 'default'} data-id="element-1393" data-mp="3vylf8dq2fvs3">
                    {t.status}
                  </Badge>
                  {!t.active && <Badge variant="default" data-id="element-1394" data-mp="339o918z4ppfp">inactiva</Badge>}
                  {!t.provider_sid && <Badge variant="warning" data-id="element-1395" data-mp="388r3jzhhgd9f">sin SID</Badge>}
                </div>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap" data-id="element-1396" data-mp="1peo9d9kxcug5">
                {t.body_preview}
              </p>
            </div>)}
        </div>
      </CardContent>
    </Card>;
  const configView = <div className="max-w-2xl space-y-4" data-id="element-1397" data-mp="1ggm1ca2rx1jd">
      <Card data-id="element-1398" data-mp="1ben0b6uiagpl">
        <CardHeader action={<Button variant="primary" size="sm" onClick={saveClinicInfo} isLoading={savingClinic} disabled={savingClinic} leftIcon={<SaveIcon className="w-3.5 h-3.5" data-id="element-1401" data-mp="16ig07uosjzfk" />} data-id="element-1400" data-mp="1bfg6o6pjz477">

              {clinicSaved ? 'Guardado ✓' : 'Guardar'}
            </Button>} data-id="element-1399" data-mp="1bewtt4erf601">
          <CardTitle subtitle="El bot usa estos datos para responder horario, ubicación y preguntas frecuentes. No los inventa." data-id="element-1402" data-mp="xnrz8gq913wk">
            Información de la clínica
          </CardTitle>
        </CardHeader>
        <CardContent data-id="element-1403" data-mp="nqdliovpmt7y">
          <div className="space-y-4" data-id="element-1404" data-mp="nplyw3k3c5tt">
            <div data-id="element-1405" data-mp="ir0vxwdgf81o">
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 flex items-center gap-1.5" data-id="element-1406" data-mp="iqisaeogprjq">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-1407" data-mp="92n90d0vtjh7" /> Horario de atención
              </label>
              <input type="text" value={clinicInfo.hours} onChange={e => setClinicInfo(c => ({
              ...c,
              hours: e.target.value
            }))} placeholder="lunes a viernes de 8:00 a.m. a 6:00 p.m. y sábados de 8:00 a.m. a 1:00 p.m." className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-cyan-500/40 focus:outline-none text-sm placeholder:text-gray-400" data-id="element-1408" data-mp="e3j9shpxspie" />
            </div>
            <div data-id="element-1409" data-mp="j24f2vk1pzdp">
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 flex items-center gap-1.5" data-id="element-1410" data-mp="j0jk54ttkefb">
                <MapPinIcon className="w-3.5 h-3.5" data-id="element-1411" data-mp="n1d79vzftp75" /> Dirección
              </label>
              <input type="text" value={clinicInfo.address} onChange={e => setClinicInfo(c => ({
              ...c,
              address: e.target.value
            }))} placeholder="Calle, número, sector, ciudad" className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-cyan-500/40 focus:outline-none text-sm placeholder:text-gray-400" data-id="element-1412" data-mp="1arz4stbtfr2w" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1413" data-mp="3a1juz2x64qi1">
              <div data-id="element-1414" data-mp="3514jkgwg5t2p">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1" data-id="element-1415" data-mp="352pc60z7b1xx">
                  Enlace de mapa (Google Maps)
                </label>
                <input type="text" value={clinicInfo.maps_url} onChange={e => setClinicInfo(c => ({
                ...c,
                maps_url: e.target.value
              }))} placeholder="https://maps.app.goo.gl/..." className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-cyan-500/40 focus:outline-none text-sm placeholder:text-gray-400" data-id="element-1416" data-mp="2cdi4ochrr20j" />
              </div>
              <div data-id="element-1417" data-mp="278fz81efasee">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1" data-id="element-1418" data-mp="278y57pqw0kzi">
                  Teléfono de la clínica
                </label>
                <input type="text" value={clinicInfo.phone} onChange={e => setClinicInfo(c => ({
                ...c,
                phone: e.target.value
              }))} placeholder="809-000-0000" className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-cyan-500/40 focus:outline-none text-sm placeholder:text-gray-400" data-id="element-1419" data-mp="2g8un98irulll" />
              </div>
              <div data-id="element-1420" data-mp="2zwbwp8btgoc8">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1" data-id="element-1421" data-mp="2zy526kap12in">
                  Métodos de pago
                </label>
                <input type="text" value={clinicInfo.payment_methods} onChange={e => setClinicInfo(c => ({
                ...c,
                payment_methods: e.target.value
              }))} placeholder="Efectivo, tarjeta y transferencia bancaria" className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-cyan-500/40 focus:outline-none text-sm placeholder:text-gray-400" data-id="element-1422" data-mp="38xs4r9f5seda" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-1423" data-mp="3a23mkj9c49fl">
        <CardHeader action={<Button variant="secondary" size="sm" onClick={() => setClinicInfo(c => ({
        ...c,
        faq: [...c.faq, {
          q: '',
          a: ''
        }]
      }))} leftIcon={<PlusIcon className="w-3.5 h-3.5" data-id="element-1426" data-mp="5nc8eu47vq4q" />} data-id="element-1425" data-mp="3a2v5oulqweoz">

              Agregar
            </Button>} data-id="element-1424" data-mp="3a2d6pq7p7mdd">
          <CardTitle subtitle="El bot responderá estas preguntas tal cual las escribas aquí." data-id="element-1427" data-mp="uaqsecmlchg">
            Preguntas frecuentes
          </CardTitle>
        </CardHeader>
        <CardContent data-id="element-1428" data-mp="tcequ46odn0a">
          <div className="space-y-3" data-id="element-1429" data-mp="tdprvw8qkgnp">
            {clinicInfo.faq.length === 0 && <p className="text-sm text-gray-400 text-center py-4" data-id="element-1430" data-mp="ol6cctqhnbg9">
                Sin preguntas frecuentes. Agrega una para que el bot la responda.
              </p>}
            {clinicInfo.faq.map((item, idx) => <div key={idx} className="rounded-xl border border-gray-100 dark:border-white/[0.06] p-3 space-y-2 bg-white/60 dark:bg-white/[0.02]" data-id="element-1431" data-mp="2nsmwaeqgyzry" data-mpi={idx}>
                <div className="flex items-start gap-2" data-id="element-1432" data-mp="291e5i2atz8d8">
                  <span className="mt-2 text-[10px] uppercase font-bold text-gray-400 flex-shrink-0" data-id="element-1433" data-mp="2e1igjcsyzaw2">P{idx + 1}</span>
                  <div className="flex-1 space-y-2 min-w-0" data-id="element-1434" data-mp="2017ow7ju5s9u">
                    <input type="text" value={item.q} onChange={e => setClinicInfo(c => {
                  const faq = [...c.faq];
                  faq[idx] = {
                    ...faq[idx],
                    q: e.target.value
                  };
                  return {
                    ...c,
                    faq
                  };
                })} placeholder="Pregunta (ej: ¿Aceptan seguro médico?)" className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-cyan-500/40 focus:outline-none text-sm font-medium placeholder:text-gray-400" data-id="element-1435" data-mp="252exg8tsyvhk" />
                    <textarea value={item.a} onChange={e => setClinicInfo(c => {
                  const faq = [...c.faq];
                  faq[idx] = {
                    ...faq[idx],
                    a: e.target.value
                  };
                  return {
                    ...c,
                    faq
                  };
                })} placeholder="Respuesta" rows={2} className="w-full resize-none px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] focus:border-cyan-500/40 focus:outline-none text-sm placeholder:text-gray-400" data-id="element-1436" data-mp="18x3q3dmd3yt9" />
                  </div>
                  <button onClick={() => confirm({
                title: 'Eliminar Pregunta',
                message: `¿Estás seguro de que deseas eliminar la pregunta "${item.q || `P${idx + 1}`}"?`,
                confirmText: 'Eliminar',
                variant: 'danger',
                onConfirm: () => setClinicInfo(c => ({
                  ...c,
                  faq: c.faq.filter((_, i) => i !== idx)
                }))
              })} title="Eliminar" className="mt-1 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 flex-shrink-0" data-id="element-1437" data-mp="c500dzjay4sg">
                    <Trash2Icon className="w-4 h-4" data-id="element-1438" data-mp="2yjax7xrfzmxb" />
                  </button>
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>
    </div>;

  // Tarjetas KPI de la cabecera. Cada una salta a la vista/filtro relevante.
  const statCards = [{
    id: 'pending',
    label: 'Esperando asesor',
    value: counts.pending,
    icon: <HandIcon className="w-4 h-4" data-id="element-1439" data-mp="3di4u1c4llu1o" />,
    tone: 'amber',
    alert: counts.pending > 0,
    onClick: () => {
      setActiveTab('conversations');
      setListFilter('pending');
    }
  }, {
    id: 'mine',
    label: 'Míos',
    value: counts.mine,
    icon: <UserIcon className="w-4 h-4" data-id="element-1440" data-mp="2d8vdtei16874" />,
    tone: 'cyan',
    alert: false,
    onClick: () => {
      setActiveTab('conversations');
      setListFilter('mine');
    }
  }, {
    id: 'bot',
    label: 'Atiende el bot',
    value: counts.bot,
    icon: <BotIcon className="w-4 h-4" data-id="element-1441" data-mp="23gpq4gytacxq" />,
    tone: 'violet',
    alert: false,
    onClick: () => {
      setActiveTab('conversations');
      setListFilter('bot');
    }
  }, {
    id: 'resolved',
    label: 'Resueltas',
    value: counts.resolved,
    icon: <CheckCircleIcon className="w-4 h-4" data-id="element-1442" data-mp="1pi9i8mp7qge0" />,
    tone: 'emerald',
    alert: false,
    onClick: () => {
      setActiveTab('conversations');
      setListFilter('resolved');
    }
  }, {
    id: 'sent',
    label: 'Enviados hoy',
    value: counts.sentToday,
    icon: <SendIcon className="w-4 h-4" data-id="element-1443" data-mp="1fmy3i4dt1969" />,
    tone: 'slate',
    alert: false,
    onClick: () => setActiveTab('campaigns')
  }];
  const toneStyles: Record<string, {
    card: string;
    icon: string;
    value: string;
  }> = {
    amber: {
      card: 'border-amber-200 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/[0.08]',
      icon: 'bg-amber-400/20 text-amber-600 dark:text-amber-300',
      value: 'text-amber-700 dark:text-amber-300'
    },
    cyan: {
      card: 'border-cyan-200 dark:border-cyan-500/25 bg-cyan-50/70 dark:bg-cyan-500/[0.07]',
      icon: 'bg-cyan-400/20 text-cyan-600 dark:text-cyan-300',
      value: 'text-cyan-700 dark:text-cyan-300'
    },
    violet: {
      card: 'border-violet-200 dark:border-violet-500/25 bg-violet-50/70 dark:bg-violet-500/[0.07]',
      icon: 'bg-violet-400/20 text-violet-600 dark:text-violet-300',
      value: 'text-violet-700 dark:text-violet-300'
    },
    emerald: {
      card: 'border-emerald-200 dark:border-emerald-500/25 bg-emerald-50/70 dark:bg-emerald-500/[0.07]',
      icon: 'bg-emerald-400/20 text-emerald-600 dark:text-emerald-300',
      value: 'text-emerald-700 dark:text-emerald-300'
    },
    slate: {
      card: 'border-gray-200 dark:border-white/[0.08] bg-gray-50/70 dark:bg-white/[0.03]',
      icon: 'bg-gray-400/20 text-gray-600 dark:text-gray-300',
      value: 'text-gray-700 dark:text-gray-200'
    }
  };
  return <div className="space-y-2.5 md:-mb-12" data-id="element-1444" data-mp="z3wbtz72tkb4">
      {/* Cabecera hero (compacta) */}
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-600 via-teal-600 to-emerald-600 px-4 py-2.5 shadow-md ${selectedConvId && activeTab === 'conversations' ? 'hidden lg:block' : ''}`} data-id="element-1445" data-mp="z5zdxhli73w7">
        <div className="absolute -top-10 -right-6 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" data-id="element-1446" data-mp="1wrn61qaxf01x" />
        <div className="relative flex items-center justify-between gap-3 flex-wrap" data-id="element-1447" data-mp="1i3ab8wnkrn90">
          <div className="flex items-center gap-2.5" data-id="element-1448" data-mp="1n1k6165e0lu3">
            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25" data-id="element-1449" data-mp="1n08ziykk4zza">
              <BotIcon className="w-5 h-5 text-white" data-id="element-1450" data-mp="2fi4mowihi56x" />
            </div>
            <div data-id="element-1451" data-mp="2fjfrmzno1izm">
              <h1 className="text-base font-bold text-white tracking-tight leading-tight" data-id="element-1452" data-mp="2fjzaqjp1ydf8">Bot Savidna</h1>
              <p className="text-[11px] text-white/80 leading-tight" data-id="element-1453" data-mp="2kf0lwti1npod">Asistente “Savi” · WhatsApp · atención y ventas</p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-1454" data-mp="2gc3vwhq6crae">
            {counts.pending > 0 && <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-300 text-amber-950 text-xs font-bold shadow-sm" data-id="element-1455" data-mp="2bf54i6xwayfw">
                <span className="relative flex h-2 w-2" data-id="element-1456" data-mp="26gk3446wq8pv">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-600/60" data-id="element-1457" data-mp="21oah2z8wxssv" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-700" data-id="element-1458" data-mp="1wqha43zscurj" />
                </span>
                {counts.pending} esperando asesor
              </span>}
            <button onClick={refreshAll} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-semibold transition-colors" data-id="element-1459" data-mp="1np11a0omd0pf">
              <RefreshCwIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} data-id="element-1460" data-mp="3udjjbbxx39s9" />
              Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* Tarjetas KPI (compactas, horizontales) */}
      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 ${selectedConvId && activeTab === 'conversations' ? 'hidden lg:grid' : ''}`} data-id="element-1461" data-mp="874t9z00v0pz">
        {statCards.map(s => {
        const t = toneStyles[s.tone];
        return <button key={s.id} onClick={s.onClick} className={`relative flex items-center gap-2.5 text-left rounded-xl border px-3 py-2 transition-all hover:shadow-md hover:-translate-y-0.5 ${t.card} ${s.alert ? 'ring-2 ring-amber-400/60' : ''}`} data-id="element-1462" data-mp="3bkfnbjx88kuh">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${t.icon}`} data-id="element-1463" data-mp="3pe6wusayk4q7">
                {s.icon}
              </div>
              <div className="min-w-0" data-id="element-1464" data-mp="2sty5wbhuhbof">
                <p className={`text-lg font-bold leading-none ${t.value}`} data-id="element-1465" data-mp="2wwuklr49knn3">{s.value}</p>
                <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mt-0.5 truncate" data-id="element-1466" data-mp="2dshnhh6q59y9">{s.label}</p>
              </div>
              {s.alert && <span className="absolute top-1.5 right-1.5 flex h-2 w-2" data-id="element-1467" data-mp="28vheaxekvr2x">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-1468" data-mp="2njvra15pkvx7" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" data-id="element-1469" data-mp="2iqsxbrx0rkgh" />
                </span>}
            </button>;
      })}
      </div>

      <div className={selectedConvId && activeTab === 'conversations' ? 'hidden lg:block' : ''} data-id="element-1470" data-mp="2rkxka0918bqq">
      <Tabs activeTab={activeTab} onChange={setActiveTab} tabs={[{
        id: 'conversations',
        label: 'Conversaciones',
        icon: <MessageSquareIcon className="w-4 h-4" data-id="element-1472" data-mp="31jwqzda8lxb3" />,
        badge: counts.pending > 0 ? counts.pending : undefined
      }, {
        id: 'campaigns',
        label: 'Campañas',
        icon: <SendIcon className="w-4 h-4" data-id="element-1473" data-mp="175m59doxvj9j" />
      }, {
        id: 'templates',
        label: 'Plantillas',
        icon: <ClockIcon className="w-4 h-4" data-id="element-1474" data-mp="3it8uja1lpg02" />
      }, {
        id: 'config',
        label: 'Configuración',
        icon: <SettingsIcon className="w-4 h-4" data-id="element-1475" data-mp="3nqhgllpve46l" />
      }]} data-id="element-1471" data-mp="36i83jvrx2lx0" />
      </div>


      {activeTab === 'conversations' && conversationsView}
      {activeTab === 'campaigns' && campaignsView}
      {activeTab === 'templates' && templatesView}
      {activeTab === 'config' && configView}

      {/* Portales fuera del árbol del Card para escapar overflow:hidden */}
      {assignPortal}
      {morePortal}
      {quickRepliesPortal}
    </div>;
}